/**
 * Workflow API client — matches existing Axios pattern.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

// ── Workflows ──────────────────────────────────────────────────────────────

export const listWorkflows = () =>
  axios.get(`${API}/workflows`, authHeaders());

export const getWorkflow = (id) =>
  axios.get(`${API}/workflows/${id}`, authHeaders());

export const createWorkflow = (data) =>
  axios.post(`${API}/workflows`, data, authHeaders());

export const updateWorkflow = (id, data) =>
  axios.put(`${API}/workflows/${id}`, data, authHeaders());

export const deleteWorkflow = (id) =>
  axios.delete(`${API}/workflows/${id}`, authHeaders());

// ── Execution ──────────────────────────────────────────────────────────────

export const executeWorkflow = (id, triggerData = {}) =>
  axios.post(`${API}/workflows/${id}/execute`, { trigger_data: triggerData }, authHeaders());

export const listExecutions = (workflowId) =>
  axios.get(`${API}/workflows/${workflowId}/executions`, authHeaders());

export const getExecution = (executionId) =>
  axios.get(`${API}/workflows/executions/${executionId}`, authHeaders());

export const getExecutionLogs = (executionId) =>
  axios.get(`${API}/workflows/executions/${executionId}/logs`, authHeaders());

// ── Node Types ─────────────────────────────────────────────────────────────

export const getNodeTypes = () =>
  axios.get(`${API}/workflows/node-types`, authHeaders());

export const listMarketplaceAgents = () =>
  axios.get(`${API}/workflows/agents`, authHeaders());

// ── Credentials ────────────────────────────────────────────────────────────

export const listCredentials = () =>
  axios.get(`${API}/workflow-credentials`, authHeaders());

export const createCredential = (data) =>
  axios.post(`${API}/workflow-credentials`, data, authHeaders());

export const updateCredential = (id, data) =>
  axios.put(`${API}/workflow-credentials/${id}`, data, authHeaders());

export const deleteCredential = (id) =>
  axios.delete(`${API}/workflow-credentials/${id}`, authHeaders());

// ── WebSocket ──────────────────────────────────────────────────────────────

/**
 * Shared WebSocket URL builder.
 * Handles two environment concerns in one place:
 *
 * 1. Mixed-Content upgrade: browsers block ws:// inside https:// pages.
 *    If the app is served over HTTPS (ngrok, production), we upgrade to wss://.
 *
 * 2. Double-slash sanitisation: REACT_APP_BACKEND_URL sometimes has a trailing
 *    slash, producing `ws://host//ws/...`. The regex collapses those while
 *    preserving the legitimate `://` in the protocol (hence the `[^:]` guard).
 */
function buildWsUrl(path) {
  let wsBase = process.env.REACT_APP_BACKEND_URL?.replace(/^http/, 'ws') || 'ws://localhost:8000';

  // Upgrade ws:// → wss:// when page is served over HTTPS (ngrok / production).
  // Modern browsers raise a Mixed Content error if we don't do this.
  if (window.location.protocol === 'https:' && wsBase.startsWith('ws://')) {
    wsBase = wsBase.replace(/^ws:\/\//, 'wss://');
  }

  // Collapse accidental double-slashes in the path portion.
  // [^:] ensures we don't collapse the '//' that's part of 'ws://' or 'wss://'.
  return `${wsBase}${path}`.replace(/([^:]\/)\/+/g, '$1');
}

export const createExecutionWS = (executionId) => {
  return new WebSocket(buildWsUrl(`/ws/executions/${executionId}`));
};

export const createWorkflowWS = (workflowId) => {
  return new WebSocket(buildWsUrl(`/ws/workflows/${workflowId}`));
};

//--- Scheduler --------------------------------------------------------------------

export const getSchedulerStatus = () =>
  axios.get(`${API}/workflows/scheduler/status`, authHeaders());

export const validateCron = (expression) =>
  axios.post(`${API}/workflows/validate-cron`, { expression }, authHeaders());

export const getJobInfo = (workflowId) =>
  axios.get(`${API}/workflows/${workflowId}/schedule`, authHeaders());

export const getRunHistory = (workflowId, limit = 20) =>
  axios.get(`${API}/workflows/${workflowId}/history?limit=${limit}`, authHeaders());

export const pauseWorkflow = (workflowId) =>
  axios.post(`${API}/workflows/${workflowId}/pause`, {}, authHeaders());

export const resumeWorkflow = (workflowId) =>
  axios.post(`${API}/workflows/${workflowId}/resume`, {}, authHeaders());

export const triggerNow = (workflowId) =>
  axios.post(`${API}/workflows/${workflowId}/trigger-now`, {}, authHeaders());

// Workflow actions
export const cloneWorkflow = (workflowId, newName) =>
  axios.post(`${API}/workflows/${workflowId}/clone`, { new_name: newName }, authHeaders());

export const restoreWorkflow = (workflowId) =>
  axios.post(`${API}/workflows/${workflowId}/restore`, {}, authHeaders());

export const getWorkflowStats = (workflowId) =>
  axios.get(`${API}/workflows/${workflowId}/stats`, authHeaders());

export const bulkActivate = (workflowIds, isActive) =>
  axios.post(`${API}/workflows/bulk-activate`, { workflow_ids: workflowIds, is_active: isActive }, authHeaders());

// Execution extras
export const cancelExecution = (workflowId, executionId) =>
  axios.post(`${API}/workflows/${workflowId}/cancel?execution_id=${executionId}`, {}, authHeaders());

export const retryExecution = (executionId) =>
  axios.post(`${API}/workflows/executions/${executionId}/retry`, {}, authHeaders());

export const resumeFromNode = (executionId, nodeId) =>
  axios.post(`${API}/workflows/executions/${executionId}/resume-from/${nodeId}`, {}, authHeaders());

export const getExecutionOutput = (executionId) =>
  axios.get(`${API}/workflows/executions/${executionId}/output`, authHeaders());

// Filtered executions
export const listExecutionsFiltered = (workflowId, params = {}) =>
  axios.get(`${API}/workflows/${workflowId}/executions`, { ...authHeaders(), params });

// ── AI Workflow Generation ────────────────────────────────────────────────
export const generateWorkflowAI = (prompt, context = {}) =>
  axios.post(`${API}/workflow/ai/generate`, { prompt, context }, authHeaders());

// ── Autonomous Engine ────────────────────────────────────────────────────────

/** Fetch all ReAct trace steps (Thought/Action/Observation) for an execution. */
export const getAgentSpans = (executionId) =>
  axios.get(`${API}/workflows/executions/${executionId}/agent-spans`, authHeaders());


// ── Webhook Schema Discovery (Listen Mode) ──────────────────────────────────

/**
 * Start listen mode: workflow captures the next incoming webhook payload
 * WITHOUT executing. Returns { listen_id, webhook_url, expires_at, status }.
 * The webhook_url no longer contains the secret \u2014 callers must send
 *   Authorization: Bearer <secret>
 * in their HTTP request headers.
 */
export const startWebhookListen = (workflowId) =>
  axios.post(`${API}/workflows/${workflowId}/webhook-listen-start`, {}, authHeaders());

/** Cancel an active listen session. */
export const stopWebhookListen = (workflowId) =>
  axios.post(`${API}/workflows/${workflowId}/webhook-listen-stop`, {}, authHeaders());

/**
 * Poll for listen session status.
 * Response: { status: "waiting"|"captured"|"expired"|"cancelled", payload, schema }.
 * Frontend polls this every 2 s after startWebhookListen().
 */
export const getWebhookListenStatus = (workflowId) =>
  axios.get(`${API}/workflows/${workflowId}/webhook-listen-status`, authHeaders());

/**
 * Inject a sample JSON body directly into the listen session
 * (useful when the user has an example payload and doesn't want to fire a real webhook).
 * No workflow execution is triggered.
 */
export const injectWebhookPayload = (workflowId, body) =>
  axios.post(`${API}/workflows/${workflowId}/webhook-listen-inject`, { body }, authHeaders());

/**
 * Fetch the schema tree derived from the last captured/real webhook payload.
 * Returns { fields: [...], raw_sample: {...} } for the Variable Picker.
 */
export const getWebhookSchema = (workflowId) =>
  axios.get(`${API}/workflows/${workflowId}/webhook-schema`, authHeaders());
